import { EntityType } from '@/plugins/cad/module/CADModule/model/common/constant'
import type { ILine, IPoint } from '@/plugins/cad/module/CADModule/model/common/interface'
import { BaseEntity } from './BaseEntity'

const BULGE_EPSILON = 1e-10
const ARC_SAMPLE_STEP = Math.PI / 16

/**
 * 多段线顶点。
 */
export interface IPolylineVertice {
  /** 横坐标 */
  x: number
  /** 纵坐标 */
  y: number
  /** 凸度，0 表示直线段，非 0 表示圆弧段 */
  bulge: number
}

interface BulgeArcData {
  center: IPoint
  radius: number
  startAngle: number
  sweepAngle: number
}

/**
 * 多段线实体，由若干顶点按序组成，可选闭合。
 */
export class PolylineEntity extends BaseEntity {
  /** 多段线顶点列表；当前仅支持直线段 */
  vertices: IPolylineVertice[] = []
  /** 是否闭合 */
  isClosed: boolean = false

  protected readonly serializeKeys: string[] = ['vertices', 'isClosed']

  constructor(data: Partial<PolylineEntity>) {
    super(EntityType.Polyline)
    Object.assign(this, data)
  }

  /**
   * 获取捕捉数据，包含端点、节点、中点与线段。
   *
   * @returns 包含端点、中点、节点与线段的捕捉数据
   */
  getCaptureData() {
    const endPoints: IPoint[] = []
    const midPoints: IPoint[] = []
    const nodePoints: IPoint[] = []
    const lines: ILine[] = []

    for (let i = 0; i < this.vertices.length; i++) {
      const v = this.vertices[i]
      if (!this.isClosed && (i === 0 || i === this.vertices.length - 1)) {
        // 添加起点作为捕捉点
        endPoints.push({ x: v.x, y: v.y })
      } else {
        // 添加节点作为捕捉点
        nodePoints.push({ x: v.x, y: v.y })
      }
    }

    const segmentCount =
      this.isClosed && this.vertices.length > 1
        ? this.vertices.length
        : Math.max(0, this.vertices.length - 1)
    for (let i = 0; i < segmentCount; i++) {
      const v1 = this.vertices[i]
      const v2 = this.vertices[(i + 1) % this.vertices.length]
      this.addSegmentCaptureData(v1, v2, midPoints, lines)
    }

    return {
      endPoints,
      midPoints,
      nodePoints,
      lines,
    }
  }

  private addSegmentCaptureData(
    v1: IPolylineVertice,
    v2: IPolylineVertice,
    midPoints: IPoint[],
    lines: ILine[]
  ): void {
    const startPoint = { x: v1.x, y: v1.y }
    const endPoint = { x: v2.x, y: v2.y }
    const bulge = v1.bulge || 0
    const arcData = this.getBulgeArcData(startPoint, endPoint, bulge)

    if (!arcData) {
      lines.push({ startPoint, endPoint })
      midPoints.push({
        x: (v1.x + v2.x) / 2,
        y: (v1.y + v2.y) / 2,
      })
      return
    }

    midPoints.push(this.getArcPointAt(arcData, 0.5))
    const segments = Math.max(2, Math.ceil(Math.abs(arcData.sweepAngle) / ARC_SAMPLE_STEP))
    let previous = startPoint
    for (let index = 1; index <= segments; index++) {
      const point = index === segments ? endPoint : this.getArcPointAt(arcData, index / segments)
      lines.push({
        startPoint: previous,
        endPoint: point,
      })
      previous = point
    }
  }

  private getBulgeArcData(
    startPoint: IPoint,
    endPoint: IPoint,
    bulge: number
  ): BulgeArcData | null {
    if (Math.abs(bulge) < BULGE_EPSILON) {
      return null
    }

    const dx = endPoint.x - startPoint.x
    const dy = endPoint.y - startPoint.y
    const chordLength = Math.hypot(dx, dy)
    if (chordLength < BULGE_EPSILON) {
      return null
    }

    const midX = (startPoint.x + endPoint.x) / 2
    const midY = (startPoint.y + endPoint.y) / 2
    const normalX = -dy / chordLength
    const normalY = dx / chordLength
    const offset = ((chordLength / 2) * (1 - bulge * bulge)) / (2 * bulge)
    const center = {
      x: midX + normalX * offset,
      y: midY + normalY * offset,
    }
    const radius = Math.hypot(startPoint.x - center.x, startPoint.y - center.y)
    if (radius < BULGE_EPSILON) {
      return null
    }

    return {
      center,
      radius,
      startAngle: Math.atan2(startPoint.y - center.y, startPoint.x - center.x),
      sweepAngle: 4 * Math.atan(bulge),
    }
  }

  private getArcPointAt(arcData: BulgeArcData, t: number): IPoint {
    const angle = arcData.startAngle + arcData.sweepAngle * t
    return {
      x: arcData.center.x + arcData.radius * Math.cos(angle),
      y: arcData.center.y + arcData.radius * Math.sin(angle),
    }
  }
}
